import { Checkbox, Group, Menu, Text, Tooltip, UnstyledButton } from '@mantine/core';
import type { CSSObject } from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DsAiCell,
	DsAiGridRow,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/DataGrid/DataGrid.styles';
import { STATUS_TONE_COLORS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/statusTone.utils';
import { DS_AI_MENU_PROPS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/dropdownMenu.styles';
import { DS_AI_TOOLTIP_PROPS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useCoveredMenu } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/useStackedDialogCover';
import type { LibraryRowView } from '../../useLibraryPicker.types';
import { LIBRARY_COLUMNS, NAME_LABEL_STYLES } from './LibraryPanel.styles';

const { colors } = defaultTheme.v3;

const ACTIONS_TRIGGER_STYLES = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: '8px',
	padding: '4px 8px',
	border: 'none',
	borderRadius: '4px',
	background: 'transparent',
	font: 'inherit',
	fontSize: '0.875rem',
	color: colors.dark[9],
	cursor: 'pointer',
	"&:hover:not(:disabled), &[aria-expanded='true']": { background: colors.gray[2] },
	'&:disabled': { cursor: 'not-allowed', opacity: 0.4 },
} satisfies CSSObject;

interface Props {
	row: LibraryRowView;
	onToggle: (documentId: string) => void;
}

/** One library document: selectable when it is a PDF, with its counted pages and the batches that read it. */
export const LibraryRow = ({ row, onToggle }: Props) => {
	const tCapManage = useTranslationCapManage();
	const actionsMenu = useCoveredMenu();
	const untitled = tCapManage('dealstack_ai.ingest.untitled_batch');
	const unsupportedLabel = row.unsupportedKey === null ? null : tCapManage(row.unsupportedKey);

	return (
		<DsAiGridRow
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_ROW}
			data-selected={row.selected}
			$template={LIBRARY_COLUMNS}
			$tinted={row.selected}
			$lightDivider
		>
			<DsAiCell $flex>
				<Group
					component="label"
					gap={12}
					miw={0}
					wrap="nowrap"
					title={row.name}
					styles={{ root: NAME_LABEL_STYLES }}
				>
					<Checkbox
						size="xs"
						radius="sm"
						aria-label={tCapManage('dealstack_ai.ingest.library.select_row', {
							name: row.name,
						})}
						checked={row.selected}
						disabled={!row.selectable}
						onChange={() => onToggle(row.id)}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_ROW_CHECKBOX}
					/>
					<Text span inherit truncate>
						{row.name}
					</Text>
				</Group>
				{row.analysedIn.length > 0 && (
					<Tooltip
						{...DS_AI_TOOLTIP_PROPS}
						withArrow
						label={row.analysedIn.map((batch) => batch.title ?? untitled).join(', ')}
					>
						<span>
							<StatusBadge
								{...STATUS_TONE_COLORS.muted}
								size="xs"
								text={tCapManage('dealstack_ai.ingest.library.analysed', {
									count: row.analysedIn.length,
								})}
							/>
						</span>
					</Tooltip>
				)}
			</DsAiCell>
			<DsAiCell>
				<Text span inherit truncate title={row.classifications.join(', ')}>
					{row.classifications.join(', ')}
				</Text>
			</DsAiCell>
			<DsAiCell $flex>
				{row.relatedPeople.map((person) => (
					<Group key={person.personId} component="span" gap={4} miw={0} wrap="nowrap">
						<Text span inherit truncate>
							{person.name}
						</Text>
					</Group>
				))}
			</DsAiCell>
			<DsAiCell>{row.lastUpdatedLabel}</DsAiCell>
			<DsAiCell>
				<Group
					component="span"
					gap={4}
					miw={0}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_ROW_PAGES}
				>
					{unsupportedLabel && (
						<StatusBadge
							{...STATUS_TONE_COLORS.muted}
							size="xs"
							text={unsupportedLabel}
						/>
					)}
					{row.pages.pages && (
						<span>{tCapManage(row.pages.pages.key, row.pages.pages.values)}</span>
					)}
					{row.pages.warnings.map((warning) => (
						<StatusBadge
							key={warning.key}
							{...STATUS_TONE_COLORS.warning}
							size="xs"
							text={tCapManage(warning.key, warning.values)}
						/>
					))}
				</Group>
			</DsAiCell>
			<DsAiCell $flex $align="center">
				<Menu {...DS_AI_MENU_PROPS} {...actionsMenu}>
					<Menu.Target>
						<UnstyledButton
							type="button"
							disabled={!row.selectable}
							styles={{ root: ACTIONS_TRIGGER_STYLES }}
						>
							{tCapManage('dealstack_ai.ingest.library.actions')}
							<Icon variant="caret-down" size="xl" />
						</UnstyledButton>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item onClick={() => onToggle(row.id)}>
							{tCapManage(
								row.selected
									? 'dealstack_ai.ingest.library.deselect'
									: 'dealstack_ai.ingest.library.select'
							)}
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
			</DsAiCell>
		</DsAiGridRow>
	);
};
